import {
  compactCents,
  formatBps,
  formatCompetenceShort,
} from "../../../utils/directorFormat";

// Frases-resposta das quatro telas principais da Diretoria (change 12, D9). Funções puras com
// modelos fixos por tela, montadas a partir do payload do BE. Nada aqui é gerado por IA: cada
// frase sai de um dos casos abaixo (saudável, desencaixe, dados insuficientes) e, quando o
// diretor não enxerga todas as obras, ganha o prefixo de escopo parcial.

const isCents = (value) => Number.isSafeInteger(value);
const money = (cents) => compactCents(cents);
const month = (value) => (typeof value === "string" && /^\d{4}-(0[1-9]|1[0-2])$/.test(value)
  ? formatCompetenceShort(value)
  : null);

const lowerFirst = (text) => text.charAt(0).toLowerCase() + text.slice(1);

// Escopo parcial: a frase fala só das obras que o diretor enxerga.
const withScope = (text, { partialScope = false } = {}) => (
  partialScope ? `Nas obras que você enxerga, ${lowerFirst(text)}` : text
);

const listNames = (names) => (names.length > 1
  ? `${names.slice(0, -1).join(", ")} e ${names[names.length - 1]}`
  : names[0]);

const shiftCompetence = (value, delta) => {
  const [year, monthNumber] = value.split("-").map(Number);
  const ordinal = year * 12 + monthNumber - 1 + delta;
  return `${Math.floor(ordinal / 12)}-${String((ordinal % 12) + 1).padStart(2, "0")}`;
};

// --- Visão geral -----------------------------------------------------------------------------

// Primeiro mês em que o acumulado da obra fica negativo; sem a série, o pior mês.
const firstUncoveredMonth = (gap) => {
  const first = (gap?.next || []).find((item) => isCents(item?.cumCents) && item.cumCents < 0);
  return first?.month ?? gap?.worstMonth ?? null;
};

const hasGap = (obra) => isCents(obra?.metrics?.gap?.worstCents) && obra.metrics.gap.worstCents < 0;

/**
 * "A Elizabeth II se paga. A Charles II fica descoberta a partir de mar/27 e precisa de
 * R$ 3,2 mi de aporte no pior mês."
 */
export const dashboardAnswer = (data, options = {}) => {
  const obras = Array.isArray(data?.obras) ? data.obras : [];
  if (!data || obras.length === 0 || data.blocks?.liquidity?.available === false) {
    return withScope("Ainda não há dados suficientes para dizer se as obras se pagam.", options);
  }
  const covered = obras.filter((obra) => !hasGap(obra));
  const uncovered = obras.filter(hasGap)
    .sort((left, right) => left.metrics.gap.worstCents - right.metrics.gap.worstCents);
  const sentences = [];
  if (covered.length > 0) {
    const profitable = covered.filter((obra) => isCents(obra.metrics?.totalCents) && obra.metrics.totalCents > 0);
    const verb = profitable.length === covered.length ? ["dá lucro", "dão lucro"] : ["se paga", "se pagam"];
    if (covered.length <= 2) {
      const names = covered.map((obra) => `a ${obra.name}`);
      sentences.push(`${listNames(names).replace(/^a /, "A ")} ${covered.length === 1 ? verb[0] : verb[1]} sem precisar de aporte.`);
    } else {
      sentences.push(`${covered.length} obras ${verb[1]} sem precisar de aporte.`);
    }
  }
  if (uncovered.length === 1 || uncovered.length === 2) {
    uncovered.forEach((obra) => {
      const from = month(firstUncoveredMonth(obra.metrics.gap));
      sentences.push(`A ${obra.name} fica descoberta${from ? ` a partir de ${from}` : ""} e precisa de `
        + `${money(-obra.metrics.gap.worstCents)} de aporte no pior mês.`);
    });
  } else if (uncovered.length > 2) {
    const worst = uncovered[0];
    sentences.push(`${uncovered.length} obras ficam descobertas; a que mais precisa é a ${worst.name}, `
      + `com ${money(-worst.metrics.gap.worstCents)} de aporte no pior mês.`);
  }
  return withScope(sentences.join(" "), options);
};

// --- Fluxo de caixa mensal -------------------------------------------------------------------

/**
 * Totais dos 12 meses a partir de `from` (competência AAAA-MM): entradas, saídas, saldo e o pior
 * mês (o de menor fluxo líquido). Meses sem dado no período não entram.
 */
export const cashflowTwelveMonths = (months = [], from) => {
  const list = Array.isArray(months) ? months : [];
  const start = month(from) ? from : list[0]?.competence;
  if (!start) return null;
  const end = shiftCompetence(start, 11);
  const window = list.filter((item) => item?.competence >= start && item.competence <= end);
  if (window.length === 0) return null;
  const sum = (read) => window.reduce((total, item) => (isCents(read(item)) ? total + read(item) : total), 0);
  const inflowCents = sum((item) => item.inflow?.totalCents);
  const outflowCents = sum((item) => item.outflow?.totalCents);
  const worst = window.reduce((best, item) => (
    isCents(item.netFlowCents) && (!best || item.netFlowCents < best.netFlowCents) ? item : best
  ), null);
  return {
    from: start,
    to: end,
    months: window.length,
    inflowCents,
    outflowCents,
    netCents: inflowCents - outflowCents,
    worstMonth: worst?.competence ?? null,
    worstNetCents: worst?.netFlowCents ?? null,
  };
};

/** "Nos próximos 12 meses saem R$ 2,1 mi a mais do que entram; o pior mês é mar/27." */
export const cashflowAnswer = (totals, options = {}) => {
  if (!totals || totals.months === 0) {
    return withScope("Ainda não há movimentos suficientes para projetar os próximos 12 meses.", options);
  }
  const worst = month(totals.worstMonth);
  const worstNegative = isCents(totals.worstNetCents) && totals.worstNetCents < 0;
  if (totals.netCents < 0) {
    return withScope(`Nos próximos 12 meses saem ${money(-totals.netCents)} a mais do que entram`
      + `${worst ? `; o pior mês é ${worst}` : ""}.`, options);
  }
  const tail = worstNegative && worst
    ? `, mas ${worst} fecha com ${money(totals.worstNetCents)}`
    : "; nenhum mês fecha no vermelho";
  const head = totals.netCents === 0
    ? "Nos próximos 12 meses entradas e saídas empatam"
    : `Nos próximos 12 meses entram ${money(totals.netCents)} a mais do que saem`;
  return withScope(`${head}${tail}.`, options);
};

// --- Recebíveis × compromissos ---------------------------------------------------------------

/**
 * "A carteira cobre 86% dos compromissos dos próximos 12 meses; falta cobrir R$ 2,9 mi,
 * concentrado na Charles II a partir de mar/27."
 */
export const receivablesAnswer = (data, options = {}) => {
  const summary = data?.summary;
  const months = data?.horizon?.months || 12;
  if (!summary || !isCents(summary.gapCents)) {
    return withScope("Ainda não há dados suficientes para comparar a carteira com os compromissos.", options);
  }
  const subject = options.projectName ? `As entradas previstas da ${options.projectName}` : "A carteira";
  const verb = options.projectName ? "cobrem" : "cobre";
  if (summary.gapCents <= 0) {
    const surplus = summary.gapCents < 0 ? `, com sobra de ${money(-summary.gapCents)}` : "";
    return withScope(`${subject} ${verb} todos os compromissos dos próximos ${months} meses${surplus}.`, options);
  }
  const coverage = isCents(summary.coverageBps) ? `${formatBps(summary.coverageBps)} dos` : "só parte dos";
  let where = "";
  if (options.projectName) {
    const from = month(summary.firstUncoveredMonth);
    where = from ? `, a partir de ${from}` : "";
  } else {
    const worst = (data.projects || [])
      .filter((project) => isCents(project.gapCents) && project.gapCents > 0)
      .sort((left, right) => right.gapCents - left.gapCents)[0];
    if (worst) {
      const from = month(worst.firstUncoveredMonth);
      where = `, concentrado na ${worst.name || "obra sem nome"}${from ? ` a partir de ${from}` : ""}`;
    }
  }
  return withScope(`${subject} ${verb} ${coverage} compromissos dos próximos ${months} meses; `
    + `falta cobrir ${money(summary.gapCents)}${where}.`, options);
};

// --- Inadimplência ---------------------------------------------------------------------------

const UNAVAILABLE_STATES = new Set(["restricted", "unavailable", "empty"]);

/** "R$ 412 mil vencidos com 23 clientes; 61% há mais de 90 dias." */
export const delinquencyAnswer = (data, options = {}) => {
  const summary = data?.summary;
  if (!data || UNAVAILABLE_STATES.has(data.state) || !summary || !isCents(summary.overdueCents)) {
    return withScope("Sem dados de parcelas a receber suficientes para medir a inadimplência.", options);
  }
  if (summary.overdueCents <= 0) {
    return withScope("Nenhuma parcela vencida em aberto.", options);
  }
  const customers = Number.isSafeInteger(summary.customersOverdue)
    ? ` com ${summary.customersOverdue} ${summary.customersOverdue === 1 ? "cliente" : "clientes"}`
    : "";
  const over90 = isCents(summary.over90Cents) && summary.over90Cents > 0
    ? `; ${formatBps(Math.round((summary.over90Cents * 10000) / summary.overdueCents))} há mais de 90 dias`
    : "; nada há mais de 90 dias";
  return withScope(`${money(summary.overdueCents)} vencidos${customers}${over90}.`, options);
};
